import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Grid, 
  List, 
  Star,
  Tag,
  Calendar,
  Edit,
  Trash2,
  Book,
  Folder,
  X,
  Menu
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import FolderManager from './FolderManager';

const Notes: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>('root');
  const [showFolders, setShowFolders] = useState(false); // Changed default to false for mobile
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const mockNotes = [
    {
      id: '1',
      title: 'Advanced React Patterns',
      content: 'Understanding compound components, render props, and higher-order components...',
      tags: ['React', 'JavaScript', 'Frontend'],
      updatedAt: '2 hours ago',
      isStarred: true,
      links: ['2', '3'],
      folderId: 'react'
    },
    {
      id: '2',
      title: 'Machine Learning Fundamentals',
      content: 'Introduction to supervised and unsupervised learning algorithms...',
      tags: ['ML', 'Python', 'AI'],
      updatedAt: '5 hours ago',
      isStarred: false,
      links: ['1'],
      folderId: 'root'
    },
    {
      id: '3',
      title: 'Database Design Principles',
      content: 'Normalization, ACID properties, and relationship modeling...',
      tags: ['Database', 'SQL', 'Backend'],
      updatedAt: '1 day ago',
      isStarred: true,
      links: ['1', '4'],
      folderId: 'root'
    },
    {
      id: '4',
      title: 'UI/UX Best Practices',
      content: 'Design systems, accessibility, and user research methods...',
      tags: ['Design', 'UX', 'Research'],
      updatedAt: '2 days ago',
      isStarred: false,
      links: ['3'],
      folderId: 'design'
    },
    {
      id: '5',
      title: 'React Hooks Deep Dive',
      content: 'useState, useEffect, useContext, and custom hooks...',
      tags: ['React', 'Hooks', 'JavaScript'],
      updatedAt: '3 days ago',
      isStarred: true,
      links: ['1'],
      folderId: 'react'
    }
  ];

  const allTags = Array.from(new Set(mockNotes.flatMap(note => note.tags)));

  const filteredNotes = mockNotes.filter(note => {
    const matchesSearch = note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         note.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = !selectedTag || note.tags.includes(selectedTag);
    const matchesFolder = !selectedFolderId || selectedFolderId === 'root' || note.folderId === selectedFolderId;
    return matchesSearch && matchesTag && matchesFolder;
  });

  const handleEditNote = (noteId: string) => {
    navigate(`/notes/${noteId}`);
  };

  const handleDeleteNote = (noteId: string) => {
    if (confirm('Are you sure you want to delete this note?')) {
      console.log('Delete note:', noteId);
      // Handle delete logic here
    }
  };

  const handleCreateNote = (folderId?: string) => {
    const targetFolder = folderId || selectedFolderId;
    navigate(`/notes/new${targetFolder ? `?folder=${targetFolder}` : ''}`);
  };

  const NoteCard: React.FC<{ note: typeof mockNotes[0] }> = ({ note }) => (
    <div
      className="p-4 sm:p-6 rounded-xl border transition-all hover:scale-105 cursor-pointer"
      style={{ 
        backgroundColor: colors.mantle,
        borderColor: colors.surface0
      }}
      onClick={() => handleEditNote(note.id)}
    >
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-base sm:text-lg font-semibold line-clamp-2 pr-2" style={{ color: colors.text }}>
          {note.title}
        </h3>
        <div className="flex items-center gap-1 flex-shrink-0">
          {note.isStarred && (
            <Star className="w-4 h-4 fill-current" style={{ color: colors.yellow }} />
          )}
          <div className="flex items-center gap-1">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleEditNote(note.id);
              }}
              className="p-1 rounded hover:opacity-80"
            >
              <Edit className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: colors.subtext0 }} />
            </button>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteNote(note.id);
              }}
              className="p-1 rounded hover:opacity-80"
            >
              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: colors.red }} />
            </button>
          </div>
        </div>
      </div>
      
      <p className="text-sm mb-4 line-clamp-3" style={{ color: colors.subtext0 }}>
        {note.content}
      </p>
      
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-1 sm:gap-2">
          {note.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-1 rounded cursor-pointer hover:opacity-80"
              style={{ 
                backgroundColor: colors.surface0,
                color: colors.text
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedTag(tag);
              }}
            >
              {tag}
            </span>
          ))}
          {note.tags.length > 2 && (
            <span
              className="text-xs px-2 py-1 rounded"
              style={{ 
                backgroundColor: colors.surface0,
                color: colors.subtext0
              }}
            >
              +{note.tags.length - 2}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-1 text-xs" style={{ color: colors.subtext0 }}>
          <Calendar className="w-3 h-3" />
          <span className="hidden sm:inline">{note.updatedAt}</span>
        </div>
      </div>
      
      {note.links.length > 0 && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.surface1 }}>
          <div className="text-xs" style={{ color: colors.subtext0 }}>
            Linked to {note.links.length} note{note.links.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}
    </div>
  );

  const NoteListItem: React.FC<{ note: typeof mockNotes[0] }> = ({ note }) => (
    <div
      className="p-3 sm:p-4 rounded-lg border transition-all hover:opacity-80 cursor-pointer"
      style={{ 
        backgroundColor: colors.mantle,
        borderColor: colors.surface0
      }}
      onClick={() => handleEditNote(note.id)}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <h3 className="text-base sm:text-lg font-semibold truncate" style={{ color: colors.text }}>
              {note.title}
            </h3>
            {note.isStarred && (
              <Star className="w-4 h-4 fill-current flex-shrink-0" style={{ color: colors.yellow }} />
            )}
          </div>
          
          <p className="text-sm mb-3 line-clamp-2" style={{ color: colors.subtext0 }}>
            {note.content}
          </p>
          
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="flex flex-wrap gap-1 sm:gap-2">
              {note.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2 py-1 rounded cursor-pointer hover:opacity-80"
                  style={{ 
                    backgroundColor: colors.surface0,
                    color: colors.text
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTag(tag);
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
            
            <div className="flex items-center gap-1 text-xs" style={{ color: colors.subtext0 }}>
              <Calendar className="w-3 h-3" />
              {note.updatedAt}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-1 ml-2 sm:ml-4 flex-shrink-0">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleEditNote(note.id);
            }}
            className="p-1 sm:p-2 rounded hover:opacity-80"
          >
            <Edit className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: colors.subtext0 }} />
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteNote(note.id);
            }}
            className="p-1 sm:p-2 rounded hover:opacity-80"
          >
            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: colors.red }} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* Sidebar de carpetas - oculto en móvil por defecto */}
      {showFolders && (
        <div className="lg:w-64 flex-shrink-0">
          <FolderManager
            selectedFolderId={selectedFolderId}
            onSelectFolder={setSelectedFolderId}
            onCreateNote={handleCreateNote}
          />
        </div>
      )}

      <div className="flex-1 space-y-8">
        {/* Header y Controles */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowFolders(!showFolders)}
              className="lg:hidden p-2 rounded-lg hover:opacity-80"
              style={{ backgroundColor: colors.surface0 }}
            >
              <Menu className="w-5 h-5" style={{ color: colors.text }} />
            </button>
            <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
              Notes
            </h1>
          </div>

          <button 
            onClick={() => handleCreateNote()}
            className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center gap-2"
            style={{ backgroundColor: colors.blue }}
          >
            <Plus className="w-5 h-5" />
            New Note
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: colors.subtext0 }} />
            <input
              type="text"
              placeholder="Search notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:border-opacity-100"
              style={{ 
                backgroundColor: colors.mantle,
                borderColor: colors.surface0,
                color: colors.text
              }}
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className="p-2 rounded-lg transition-all hover:opacity-80"
              style={{ 
                backgroundColor: viewMode === 'grid' ? colors.surface0 : 'transparent',
                color: viewMode === 'grid' ? colors.text : colors.subtext0
              }}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className="p-2 rounded-lg transition-all hover:opacity-80"
              style={{ 
                backgroundColor: viewMode === 'list' ? colors.surface0 : 'transparent',
                color: viewMode === 'list' ? colors.text : colors.subtext0
              }}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
              className="px-3 py-1 rounded-lg text-sm font-medium transition-all hover:opacity-80 flex items-center gap-2"
              style={{ 
                backgroundColor: selectedTag === tag ? colors.blue : colors.surface0,
                color: selectedTag === tag ? 'white' : colors.text
              }}
            >
              <Tag className="w-4 h-4" />
              {tag}
              {selectedTag === tag && (
                <X 
                  className="w-4 h-4 hover:text-red-500" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTag(null);
                  }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Notes Grid/List */}
        <div className={`grid gap-4 ${
          viewMode === 'grid' 
            ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' 
            : 'grid-cols-1'
        }`}>
          {filteredNotes.map((note) => (
            viewMode === 'grid' 
              ? <NoteCard key={note.id} note={note} />
              : <NoteListItem key={note.id} note={note} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Notes;